<template>
  <el-drawer
    :visible.sync="drawerVisible"
    :direction="direction"
    :size="`${drawerWidthPercent}%`"
    :wrapper-closable="false"
    @close="$emit('close')"
  >
    <div class="drawer-resize-handle" @mousedown="startResize"></div>
    <template #title>
      <div class="flex items-center gap-2">
        <div class="superdoc-trigger superdoc-trigger--sm cursor-pointer shadow-lg">
          <img class="superdoc-trigger__icon superdoc-trigger__icon--sm" src="@public/assets/images/ic_superdoc_ava.svg" alt="SuperDoc" />
        </div>
        <h3 class="text-black font-bold fs-18 mb-0">SuperDoc</h3>
      </div>
    </template>
    <div class="superdoc-drawer-content flex flex-1 flex-col h-full border-t border-2 border-gray-200 px-3 pb-3">
      <div class="flex flex-1 py-1">
        <div ref="messageContainer" class="h-full w-full overflow-auto" :class="{ 'welcome-centered': showWelcomeSection }">
          <SuperDocWelcome
            v-if="showWelcomeSection"
            class="message-item"
            :message="aiWelcomeMessage"
            :subtitle="$t('lbl_super_doc.lbl_input_placeholder')"
            :markdown-renderer="markdownRenderer"
          />
          <div v-if="processing" class="font-xs text-gray-600 text-xs flex items-center">
            <span>{{ $t('lbl_super_doc.lbl_initializing_msg') }}</span>
            <span class="ml-1 animate-dots">.</span>
          </div>
          <template v-for="(item, index) in messages">
            <SuperDocQuestionMessage
              :key="`q-${index}`"
              :content="item.question"
              :image-attachments="item.images"
              :file-attachments="item.files"
              @copy="copyMessage"
            />
            <div :key="`e-${index}`" v-if="item.error">
              <div v-if="item.error && !reSending && index === messages.length - 1"
                class="flex justify-content-end mb-2">
                <div class="font-xs text-gray-600 text-xs flex items-center">
                  <span class="text-red-500"><i class="el-icon-warning mr-1" />{{
                    $t('lbl_super_doc.lbl_error_occurred_msg') }} <span class="underline cursor-pointer"
                      @click="$emit('resend', item.question, index)">{{ $t('lbl_super_doc.lbl_error_try_again_msg')
                      }}</span></span>
                </div>
              </div>
              <div v-else class="flex justify-content-end mb-2">
                <div class="font-xs text-gray-600 text-xs flex items-center">
                  <span class="text-red-500"><i class="el-icon-warning mr-1" />{{
                    $t('lbl_super_doc.lbl_error_occurred_msg') }}</span>
                </div>
              </div>
            </div>
            <SuperDocAnswerMessage
              :key="`a-${index}`"
              v-if="item.answer !== ''"
              :content="item.answer"
              :markdown-renderer="markdownRenderer"
              @copy="copyMessage"
              @print="printAnswer(item.question, $event)"
              @pdf="exportAnswerPdf(item.question, $event)"
            />
          </template>
          <div v-if="thinking" class="superdoc-thinking-bubble">
            <i class="el-icon-loading"></i>
          </div>
          <div v-if="findTime && deepThinkingDurationText">
            <small class="font-xs text-gray-400 text-xs">{{ $t('lbl_super_doc.lbl_performance_msg', {
              deep_thinking_time: deepThinkingDurationText
            }) }}</small>
          </div>
        </div>
      </div>
      <div class="chat-scroll-actions">
        <button type="button" class="chat-scroll-btn" title="Cuộn xuống cuối hội thoại" aria-label="Cuộn xuống cuối hội thoại" @click="scrollToBottom">
          <i class="el-icon-arrow-down"></i>
        </button>
      </div>
      <div class="sticky bottom-0 left-0 right-0 mt-auto bg-white">
        <SuperDocInputArea
          :value="question"
          :thinking="thinking"
          :max-files="2"
          :max-images="3"
          @input="handleQuestionInput"
          @send="handleSend"
        />
        <div><small class="font-xs text-gray-400 text-xs">{{ $t('lbl_super_doc.lbl_note_msg') }}</small></div>
      </div>
    </div>
  </el-drawer>
</template>

<script>
import SuperDocWelcome from './SuperDocWelcome.vue'
import SuperDocInputArea from './SuperDocInputArea.vue'
import SuperDocQuestionMessage from './SuperDocQuestionMessage.vue'
import SuperDocAnswerMessage from './SuperDocAnswerMessage.vue'

export default {
  name: 'SuperDocDrawer',
  components: { SuperDocWelcome, SuperDocInputArea, SuperDocQuestionMessage, SuperDocAnswerMessage },
  data () {
    return {
      drawerWidthPercent: 40,
      isResizing: false
    }
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    direction: {
      type: String,
      default: 'rtl'
    },
    processing: {
      type: Boolean,
      default: false
    },
    aiWelcomeMessage: {
      type: String,
      default: ''
    },
    messages: {
      type: Array,
      default: () => []
    },
    reSending: {
      type: Boolean,
      default: false
    },
    thinking: {
      type: Boolean,
      default: false
    },
    findTime: {
      type: [String, Number],
      default: null
    },
    deepThinkingDurationText: {
      type: String,
      default: ''
    },
    question: {
      type: String,
      default: ''
    },
    markdownRenderer: {
      type: Function,
      required: true
    }
  },
  computed: {
    showWelcomeSection () {
      return this.aiWelcomeMessage !== '' && this.messages.length === 0
    },
    drawerVisible: {
      get () {
        return this.visible
      },
      set (value) {
        this.$emit('update:visible', value)
      }
    }
  },
  methods: {
    startResize (event) {
      event.preventDefault()
      this.isResizing = true
      window.addEventListener('mousemove', this.onResize)
      window.addEventListener('mouseup', this.stopResize)
    },
    onResize (event) {
      if (!this.isResizing) return

      const viewportWidth = window.innerWidth || 1
      const rawPercent = ((viewportWidth - event.clientX) / viewportWidth) * 100
      const clampedPercent = Math.max(40, Math.min(80, rawPercent))
      this.drawerWidthPercent = Math.round(clampedPercent * 10) / 10
    },
    stopResize () {
      this.isResizing = false
      window.removeEventListener('mousemove', this.onResize)
      window.removeEventListener('mouseup', this.stopResize)
    },
    handleQuestionInput (value) {
      this.$emit('update:question', value)
    },
    handleSend (payload) {
      if (this.thinking) return
      const text = (this.question || '').trim()
      const hasUploads = Boolean(payload?.files?.length || payload?.images?.length)
      if (!text && !hasUploads) return
      this.$emit('send', text, payload)
    },
    stripHtml (html) {
      const parser = document.createElement('div')
      parser.innerHTML = html || ''
      return parser.textContent || parser.innerText || ''
    },
    async copyMessage (content) {
      const plainText = this.stripHtml(content).trim()
      if (!plainText) return

      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(plainText)
        } else {
          const textArea = document.createElement('textarea')
          textArea.value = plainText
          textArea.setAttribute('readonly', '')
          textArea.style.position = 'absolute'
          textArea.style.left = '-9999px'
          document.body.appendChild(textArea)
          textArea.select()
          document.execCommand('copy')
          textArea.remove()
        }
        this.$message.success('Đã sao chép vào bộ nhớ tạm')
      } catch (error) {
        this.$message.error('Không thể sao chép vào bộ nhớ tạm')
      }
    },
    escapeHtml (text = '') {
      return String(text)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;')
    },
    createPrintWindow (title, questionText, answerHtml) {
      const printWindow = window.open('', '_blank', 'width=900,height=700')
      if (!printWindow) {
        this.$message.error('Khong mo duoc cua so in')
        return null
      }
      const safeQuestion = this.escapeHtml(this.stripHtml(questionText || '').trim())
      const questionSection = safeQuestion
        ? `
          <section class="qa-block">
            <h2>Câu hỏi:</h2>
            <div class="qa-question">${safeQuestion}</div>
          </section>
        `
        : ''

      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              * { box-sizing: border-box; }
              body { font-family: Arial, sans-serif; padding: 24px; line-height: 1.6; color: #0f172a; font-size: 14px; }
              h1 { font-size: 18px; margin: 0 0 16px; }
              h2, h3, h4 { margin: 16px 0 8px; line-height: 1.4; color: #0f172a; }
              p { margin: 0 0 10px; }
              ul, ol { margin: 0 0 10px 20px; padding: 0; }
              pre { margin: 10px 0; padding: 10px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; white-space: pre-wrap; }
              code { font-family: Menlo, Consolas, monospace; font-size: 12px; }
              table { width: 100%; border-collapse: collapse; table-layout: fixed; margin: 10px 0; border: 1px solid #cbd5e1; }
              th, td { border: 1px solid #cbd5e1; padding: 8px 10px; text-align: left; vertical-align: top; word-break: break-word; }
              th { background: #f1f5f9; font-weight: 600; color: #0f172a; }
              tr:nth-child(even) td { background: #f8fafc; }
              img { max-width: 100%; height: auto; }
              .qa-block { margin-bottom: 14px; }
              .qa-question { white-space: pre-wrap; padding: 10px 12px; background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; }
              @media print { body { padding: 12px; } }
            </style>
          </head>
          <body>
            ${questionSection}
            <section class="qa-block">
              <h2>Câu trả lời:</h2>
              <div>${answerHtml || ''}</div>
            </section>
          </body>
        </html>
      `)
      printWindow.document.close()
      return printWindow
    },
    printAnswer (question, answer) {
      const printWindow = this.createPrintWindow('SuperDoc Answer', question, this.markdownRenderer(answer))
      if (!printWindow) return
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 200)
    },
    exportAnswerPdf (question, answer) {
      const printWindow = this.createPrintWindow('SuperDoc PDF', question, this.markdownRenderer(answer))
      if (!printWindow) return
      printWindow.focus()
      setTimeout(() => {
        printWindow.print()
      }, 200)
    },
    scrollToBottom () {
      this.$nextTick(() => {
        const container = this.$refs.messageContainer
        if (container) container.scrollTop = container.scrollHeight
      })
    },
    scrollToLastMessage () {
      this.scrollToBottom()
    }
  },
  beforeDestroy () {
    this.stopResize()
  }
}
</script>

<style scoped>
.drawer-resize-handle {
  position: absolute;
  top: 0;
  left: 0;
  width: 8px;
  height: 100%;
  cursor: ew-resize;
  z-index: 10;
}

.superdoc-drawer-content {
  position: relative;
}

.chat-scroll-actions {
  position: absolute;
  left: 50%;
  bottom: 195px;
  z-index: 5;
  transform: translateX(-50%);
  opacity: 0.4;
  transition: opacity 0.2s ease;
}

.chat-scroll-actions:hover {
  opacity: 1;
}

.chat-scroll-btn {
  width: 32px;
  height: 32px;
  border: 1px solid #bfdbfe;
  border-radius: 8px;
  background: #fff;
  color: #2563eb;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.12);
}

.chat-scroll-btn:hover {
  background: #eff6ff;
}

.drawer-resize-handle::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 2px;
  transform: translateY(-50%);
  width: 2px;
  height: 80px;
  border-radius: 9999px;
  background: #cbd5e1;
  opacity: 0.8;
}

.superdoc-trigger {
  border-radius: 9999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #eef5ff 0%, #dbeafe 100%);
  border: 1px solid #bfdbfe;
}

.superdoc-trigger--sm {
  width: 35px;
  height: 35px;
}

.superdoc-trigger--lg {
  width: 64px;
  height: 64px;
}

.superdoc-trigger__icon {
  object-fit: contain;
}

.superdoc-trigger__icon--sm {
  width: 14px;
  height: 14px;
}

.superdoc-trigger__icon--lg {
  width: 38px;
  height: 38px;
}

.welcome-centered {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: 100%;
}

.superdoc-thinking-bubble {
  display: inline-flex;
  align-items: center;
  margin: 8px 0 12px;
  padding: 8px 10px;
  border-radius: 10px;
  background: #f3f4f6;
  color: #374151;
  font-size: 12px;
}

.thinking-icon-row {
  color: #6b7280;
  font-size: 18px;
  line-height: 1;
  margin: 2px 0 10px;
}

@keyframes dots {
  0% {
    content: '';
  }

  25% {
    content: '.';
  }

  50% {
    content: '..';
  }

  75% {
    content: '...';
  }

  100% {
    content: '';
  }
}

.animate-dots::after {
  content: '';
  animation: dots 1.5s steps(4, end) infinite;
}

.message-item {
  opacity: 0;
  transform: translateY(10px);
  animation: fadeSlide 0.4s ease forwards;
}

@keyframes fadeSlide {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
